import { useEffect, useState } from "react";
import { NavLink, useNavigate, useMatch } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, FolderKanban, Settings, LogOut, Menu, X, Search, Bell,
  ListChecks, Columns3, Flag, BarChart3, SlidersHorizontal, UserCog, Repeat,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import RoleBadge from "./RoleBadge";
import { toast } from "sonner";

const NAV = [
  { to: "/dashboard", label: "Visão geral", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/espacos", label: "Espaços de trabalho", icon: Building2, testid: "nav-workspaces" },
  { to: "/projetos", label: "Projetos", icon: FolderKanban, testid: "nav-projects" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, testid: "nav-reports" },
  { to: "/usuarios", label: "Usuários", icon: Users, testid: "nav-users", adminOnly: true },
  { to: "/reatribuicoes", label: "Reatribuições", icon: UserCog, testid: "nav-reassignments", adminOnly: true },
  { to: "/configuracoes", label: "Configurações", icon: Settings, testid: "nav-settings" },
];

const PROJECT_NAV = [
  { suffix: "", label: "Visão geral", icon: LayoutDashboard, testid: "pnav-overview", end: true },
  { suffix: "backlog", label: "Backlog", icon: ListChecks, testid: "pnav-backlog" },
  { suffix: "kanban", label: "Quadro Kanban", icon: Columns3, testid: "pnav-kanban" },
  { suffix: "sprints", label: "Sprints", icon: Flag, testid: "pnav-sprints" },
  { suffix: "recorrencias", label: "Recorrências", icon: Repeat, testid: "pnav-recurrences" },
  { suffix: "relatorios", label: "Relatórios", icon: BarChart3, testid: "pnav-reports" },
  { suffix: "configuracoes", label: "Configurações", icon: SlidersHorizontal, testid: "pnav-settings" },
];

export default function Layout({ children }) {
  const { user, logout, tenant, platformMode, listAvailableTenants, switchTenant } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchingId, setSwitchingId] = useState(null); // id do item em progresso

  // Fase 1I.0 — Carrega tenants disponíveis (somente modo global).
  useEffect(() => {
    if (!platformMode) { setAvailableTenants([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await listAvailableTenants();
        if (!cancelled) setAvailableTenants(Array.isArray(list) ? list : []);
      } catch { if (!cancelled) setAvailableTenants([]); }
    })();
    return () => { cancelled = true; };
  }, [platformMode, listAvailableTenants, tenant?.tenant_id]);

  const canSwitch = platformMode && availableTenants.length > 1;

  const projectMatch = useMatch("/projetos/:id/*");
  const currentProjectId = projectMatch?.params?.id;

  const items = NAV.filter((n) => !n.adminOnly || user?.role === "admin");
  // Fase 1L.8C — o item "Administração global" foi removido da sidebar
  // do tenant. A entrada no Admin Console passa exclusivamente pela
  // tela de seleção de empresas (``PlatformSelectTenant``).
  const navItems = items;

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside
        data-testid="app-sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 flex flex-col transition-transform`}
      >
        <div className="h-16 px-5 flex items-center border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-slate-900 grid place-items-center text-white font-heading font-bold">R</div>
            <div className="leading-tight">
              <div className="font-heading font-semibold text-slate-900 tracking-tight">Ruma Flow</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Gestão de projetos</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={() => setOpen(false)} end={n.to === "/projetos"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }>
              <n.icon className="w-4 h-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}

          {currentProjectId && (
            <div className="pt-4" data-testid="project-nav">
              <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Projeto atual</div>
              {PROJECT_NAV.map((n) => (
                <NavLink key={n.suffix || "overview"} to={`/projetos/${currentProjectId}${n.suffix ? "/" + n.suffix : ""}`} end={n.end} data-testid={n.testid} onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`
                  }>
                  <n.icon className="w-4 h-4" strokeWidth={1.75} />
                  {n.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-slate-200 grid place-items-center text-slate-700 text-sm font-medium">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </aside>

      {open && <div className="lg:hidden fixed inset-0 bg-slate-900/30 z-30" onClick={() => setOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-white/85 backdrop-blur border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
          <button data-testid="sidebar-toggle" className="lg:hidden p-2 rounded-md hover:bg-slate-100" onClick={() => setOpen((o) => !o)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="hidden sm:flex items-center gap-2 flex-1 max-w-md ml-2">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="global-search" placeholder="Pesquisar (em breve)" disabled
                className="w-full h-9 pl-9 pr-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-500 placeholder:text-slate-400 focus:outline-none" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="notifications-btn" className="relative p-2 rounded-md hover:bg-slate-100" title="Notificações">
              <Bell className="w-5 h-5 text-slate-600" />
            </button>
            {tenant && (
              <div
                data-testid="active-tenant-badge"
                className="hidden sm:flex items-center gap-2 px-3 h-8 rounded-md bg-slate-50 border border-slate-200 text-xs"
              >
                <span className="text-slate-500">Empresa:</span>
                <span className="font-medium text-slate-900">{tenant.name}</span>
              </div>
            )}
            {canSwitch && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setSwitcherOpen((v) => !v)}
                  className="hidden sm:inline-flex items-center gap-2 px-3 h-8 rounded-md bg-white border border-slate-200 text-xs hover:bg-slate-50"
                  data-testid="tenant-switcher-btn"
                  disabled={switching}
                >
                  <Repeat className="w-4 h-4 text-slate-500" />
                  Trocar empresa
                </button>
                {switcherOpen && (
                  <div
                    className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-md shadow-lg z-30"
                    data-testid="tenant-switcher-menu"
                  >
                    <ul className="py-1">
                      {availableTenants.map((t) => (
                        <li key={t.tenant_id}>
                          <button
                            type="button"
                            disabled={switching || t.current}
                            data-testid={`tenant-switcher-item-${t.slug}`}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between disabled:opacity-60 disabled:cursor-not-allowed ${t.current ? "font-semibold" : ""}`}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (t.current || switching) return;
                              // Fecha o menu imediatamente e marca o item
                              // em progresso para renderizar "Trocando…".
                              setSwitcherOpen(false);
                              setSwitching(true);
                              setSwitchingId(t.tenant_id);
                              try {
                                const r = await switchTenant(t.tenant_id);
                                if (r?.no_op) {
                                  // No-op: sem reload; UI já reflete o estado.
                                  return;
                                }
                                // Sucesso: navegação completa evita cache do
                                // tenant anterior. Aguarda para o browser
                                // aplicar os novos cookies.
                                window.location.assign("/dashboard");
                              } catch {
                                // Falha preserva sessão e cookies atuais.
                                // Mensagem opaca — sem detalhes internos.
                                toast.error("Não foi possível trocar de empresa. Tente novamente.");
                              } finally {
                                setSwitching(false);
                                setSwitchingId(null);
                              }
                            }}
                          >
                            <span>{t.name}</span>
                            <span className="text-[11px] text-slate-400">
                              {switchingId === t.tenant_id
                                ? "Trocando..."
                                : `${t.slug}${t.current ? " · atual" : ""}`}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {user && <RoleBadge role={user.role} />}
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 max-w-7xl w-full mx-auto" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
